import React from 'react'
import { useOutletContext } from 'react-router-dom'

import { ProfileTabsDocument } from 'generated/resources/graphql'

import i18n from 'modules/core/lib/i18n'
import { buildModuleRoute, buildNavigation } from 'modules/core/lib/react-router'

import competenciesProfileRoutes from 'modules/competencies/routes/profile'
import contractsProfileRoutes from 'modules/contracts/routes/profile'
import customResourcesProfileRoutes from 'modules/custom_resources/routes/profile'
import talentProfileRoutes from 'modules/performance_analytics/routes/profile'
import performanceProfileRoutes from 'modules/performance_v2/routes/profile'
import { RouteContext } from 'modules/profile/pages/RootPage'
import timePlanningProfileRoutes from 'modules/time_planning/routes/profile'

const PersonalPage = React.lazy(() => import('modules/profile/pages/PersonalPage'))
const ProfileOverviewPage = React.lazy(() => import('modules/profile/pages/ProfileOverviewPage'))
const ProfilePage = React.lazy(() => import('modules/profile/pages/ProfilePage'))
const RootPage = React.lazy(() => import('modules/profile/pages/RootPage'))

const IndexRootPage = () => {
  const context = useOutletContext<RouteContext>()

  if (context.canSeeOverview) {
    return <ProfileOverviewPage />
  }

  return <ProfilePage />
}

export const profileRoutes = buildNavigation((navigation) => [
  buildModuleRoute({
    path: navigation.profile.path,
    handle: {
      module: 'profile',
      moduleName: () => i18n.t('sidebar.your_profile'),
      meta: ({ watchQuery, session }) => {
        if (!session.employee) throw new Error('No employee found on Profile')

        return watchQuery({
          query: ProfileTabsDocument,
          variables: {
            id: session.employee.id,
          },
        }).map(({ data }) => {
          const canSeePerformance =
            data?.employees.employee?._policies?.seeEmployeePerformanceTab.can ?? false
          const canSeeTalent =
            data?.employees.employee?._policies?.seeEmployeeTalentPage?.can ?? false
          const canSeeCompetencies =
            data?.employees.employee?._policies?.seeEmployeeCompetenciesAssessments?.can ?? false

          return {
            title: i18n.t('sidebar.profile'),
            subNavigation: [
              {
                index: true,
                title: i18n.t('overview.title'),
                href: navigation.profile.fullPath(),
              },
              {
                title: i18n.t('profile.work_details'),
                href: navigation.profile.workDetails.fullPath(),
                isVisible: data.apiCore._policies.companySeeEmployeeProfile.can,
              },
              {
                title: i18n.t('profile.personal_details'),
                href: navigation.profile.personal.fullPath(),
                isVisible: data.employees.employee?._policies?.readPersonalInfo.can ?? false,
              },
              {
                title: i18n.t('admin_employee.contracts'),
                href: navigation.profile.contractVersions.fullPath(),
                isVisible:
                  data.employees._policies.sidebarSeeProfileContractVersionsSubLevelItem.can,
              },
              {
                title: i18n.t('sidebar.performance_new'),
                href: navigation.profile.performance.fullPath(),
                isVisible: canSeePerformance && !canSeeTalent,
              },
              {
                title: i18n.t('sidebar.talent'),
                href: navigation.profile.talent.fullPath(),
                isVisible: canSeeTalent,
              },
              {
                title: i18n.t('competencies_v2.title'),
                href: navigation.profile.competencies.fullPath(),
                isVisible: canSeeCompetencies,
              },
              {
                title: i18n.t('time_planning.title'),
                href: navigation.profile.planningVersions.fullPath(),
                isVisible: data.timePlanning._policies.planningVersionSeeOwned.can,
              },
              {
                title: i18n.t('custom_tables.header'),
                href: navigation.profile.customTables.fullPath(),
                isVisible: data.employees._policies.sidebarSeeProfileCustomTablesSubLevelItem.can,
              },
            ],
          }
        })
      },
    },
    element: <RootPage />,
    children: [
      {
        index: true,
        element: <IndexRootPage />,
      },
      {
        path: navigation.profile.personal.path,
        element: <PersonalPage />,
        handle: {
          meta: () => ({
            title: i18n.t('admin_employee.personal'),
          }),
        },
      },
      {
        path: navigation.profile.overview.path,
        element: <ProfileOverviewPage />,
        handle: {
          meta: () => ({
            title: i18n.t('admin_employee.overview'),
          }),
        },
      },
      {
        path: navigation.profile.workDetails.path,
        element: <ProfilePage />,
      },
      ...contractsProfileRoutes(navigation),
      ...customResourcesProfileRoutes(navigation),
      ...competenciesProfileRoutes(navigation),
      ...timePlanningProfileRoutes(navigation),
      ...performanceProfileRoutes(navigation),
      ...talentProfileRoutes(navigation),
    ],
  }),
])
